import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Box, Button, Divider, Popover, Stack, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { RootReducer } from '@store/index';
import NotificationServ from '@api/Notifications/NotificationServ';
import usePushNotifications from '@hooks/usePushNotifications';
import { playNotificationSound } from '@utils/Sounds';
import { toDateSafe } from '@utils/Date';
import {
    AppNotification,
    NotificationType,
    resolveNotificationLink,
    countUnread,
    filterNotificationsForUser,
    isNotificationRead
} from '@api/Notifications/Notification';

/**
 * Campana de notificaciones internas de la barra superior.
 *
 * **La notificación es el DATO; el push es TRANSPORTE.** Aquí se ve lo que está
 * guardado en Firestore, llegara o no el push: si la tablet estaba apagada o el
 * permiso denegado, el aviso sigue aquí. Ése es el punto de todo el diseño.
 *
 * ⚠️ **Se monta UNA sola vez** (vive en la barra, que está en todas las
 * pantallas) y su `onSnapshot` va acotado por ventana de retención y por
 * `limit`: un listener cobra por documento **en cada entrega**, no solo al
 * abrir.
 *
 * El filtro de audiencia se resuelve aquí, en el cliente, porque Firestore no
 * sabe hacer OR entre campos distintos sin índices compuestos.
 */

const ICONOS: Record<NotificationType, React.ReactNode> = {
    [NotificationType.Order]: <ReceiptLongIcon fontSize="small" />,
    [NotificationType.CashRegister]: <PointOfSaleIcon fontSize="small" />,
    [NotificationType.Inventory]: <Inventory2OutlinedIcon fontSize="small" />,
    [NotificationType.System]: <InfoOutlinedIcon fontSize="small" />
};

const cuandoFue = (valor: any): string => {
    // `createdAt` llega como Timestamp, Date, string o Timestamp crudo según
    // quién lo escribió: eso lo resuelve `toDateSafe`, la única copia.
    const fecha = toDateSafe(valor);
    if (!fecha) return '';
    return formatDistanceToNow(fecha, { addSuffix: true, locale: es });
};

const NotificationsBell: React.FunctionComponent = () => {
    const navigate = useNavigate();
    const { loggedUser, branchOfficeSelected, profileSelected } = useSelector((state: RootReducer) => state.auth);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [todas, setTodas] = useState<AppNotification[]>([]);

    // Cuáles estaban sin leer al ABRIR el panel: al abrir se marcan todas como
    // leídas (el badge se limpia, como en cualquier app), pero el resaltado
    // tiene que sobrevivir a esa marca o el usuario no vería cuáles eran nuevas.
    const [nuevasAlAbrir, setNuevasAlAbrir] = useState<string[]>([]);
    const marcando = useRef<boolean>(false);
    const [readUpTo, setReadUpTo] = useState<Date | undefined>(undefined);
    const { status: pushStatus, working: pushWorking, enablePush, refreshSubscription } = usePushNotifications();

    // Ids ya vistos por esta pestaña, para sonar SOLO cuando llega algo nuevo.
    // `undefined` marca la primera entrega: al abrir la app todo es "nuevo" y
    // sonar ahí sería una alarma en cada recarga.
    const vistos = useRef<Set<string> | undefined>(undefined);

    useEffect(() => {
        if (!loggedUser?.uid) return undefined;
        const unsubscribe = NotificationServ.subscribeToNotifications(setTodas);
        return () => unsubscribe();
    }, [loggedUser?.uid]);

    // Hasta dónde leyó esta persona: 1 lectura de `notification-reads/{uid}`.
    // Sustituye al `readBy` que vivía dentro de cada aviso y que convertía cada
    // apertura de la campana en hasta 50 escrituras re-entregadas a todas las
    // campanas de la cadena (ver NOTIFICATION_READS_COLLECTION).
    useEffect(() => {
        if (!loggedUser?.uid) return;
        NotificationServ.getReadUpTo(loggedUser.uid)
            .then(setReadUpTo)
            .catch((error) => console.error('No se pudo leer hasta dónde leyó:', error));
    }, [loggedUser?.uid]);

    const mias = useMemo(
        () => (loggedUser ? filterNotificationsForUser(todas, loggedUser as any, branchOfficeSelected?.id) : []),
        [todas, loggedUser, branchOfficeSelected?.id]
    );
    const sinLeer = countUnread(mias, loggedUser?.uid, readUpTo);

    /**
     * Sonido al llegar algo nuevo **estando dentro de la app**.
     *
     * Fuera de la app el sonido lo pone el sistema operativo con el push; esto
     * cubre el otro caso: el cajero está en el POS y entra un aviso.
     *
     * ⚠️ **No suena en la primera entrega** (la carga inicial trae todo el
     * historial reciente) ni por avisos que este usuario ya leyó en otro
     * dispositivo. Y el navegador puede bloquear el audio hasta que haya habido
     * una interacción: `playNotificationSound` ya se traga ese error.
     */
    useEffect(() => {
        const idsActuales = mias.map((n) => n.id as string).filter(Boolean);
        if (vistos.current === undefined) {
            vistos.current = new Set(idsActuales);
            return;
        }
        const nuevas = mias.filter(
            (n) => n.id && !vistos.current?.has(n.id) && !isNotificationRead(n, loggedUser?.uid, readUpTo)
        );
        idsActuales.forEach((id) => vistos.current?.add(id));
        if (nuevas.length) playNotificationSound();
    }, [mias, loggedUser?.uid, readUpTo]);

    // Un dispositivo que YA dio permiso vuelve a registrarse al entrar: el
    // navegador puede rotar el endpoint, y la suscripción guarda una copia del
    // perfil y las sucursales que pudo haber cambiado.
    useEffect(() => {
        if (loggedUser?.uid) void refreshSubscription();
    }, [loggedUser?.uid, refreshSubscription]);

    const abrir = async (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        const pendientes = mias.filter((n) => !isNotificationRead(n, loggedUser?.uid, readUpTo));
        setNuevasAlAbrir(pendientes.map((n) => n.id as string).filter(Boolean));
        if (!pendientes.length || !loggedUser?.uid || marcando.current) return;
        marcando.current = true;
        // UNA escritura para todo lo anterior a este instante. El estado local
        // se adelanta para que el badge se apague ya: la marca que importa es la
        // misma fecha que se persiste, así que no hay divergencia.
        const marca = new Date();
        setReadUpTo(marca);
        try {
            await NotificationServ.markAllAsRead(loggedUser.uid, marca);
        } catch (error) {
            // Que no se marque como leída no puede romper la barra: el aviso
            // sigue visible y se reintentará en la próxima apertura.
            console.error('No se pudieron marcar como leídas:', error);
        }
        marcando.current = false;
    };

    /**
     * Abre el destino del aviso.
     *
     * ⚠️ **El link se guarda SIN el perfil y se resuelve aquí.** Las rutas del
     * admin viven bajo `/{profileSelected}/…`, así que un `/orders` pelado no
     * existe: cae en el catch-all del router, que redirige al home — y el home
     * del admin es el Dashboard. Ése era el motivo de que TODAS las
     * notificaciones aterrizaran ahí sin importar a dónde apuntaran.
     *
     * Se guarda sin perfil a propósito: el mismo aviso lo pueden recibir un
     * admin y un gerente, y cada uno tiene su propio prefijo.
     */
    /**
     * Un push abierto desde el sistema operativo llega como `/?goto=/orders`: el
     * service worker no puede saber el perfil de quien abre, así que manda el
     * destino y aquí se resuelve. Se limpia el parámetro para que una recarga no
     * vuelva a navegar.
     */
    useEffect(() => {
        const goto = new URLSearchParams(window.location.search).get('goto');
        if (!goto || !profileSelected) return;
        window.history.replaceState({}, '', window.location.pathname);
        navigate(resolveNotificationLink(goto, profileSelected));
    }, [profileSelected, navigate]);

    const alTocar = (notification: AppNotification) => {
        setAnchorEl(null);
        if (!notification.link) return;
        navigate(resolveNotificationLink(notification.link, profileSelected));
    };

    return (
        <>
            <IconButton color="secondary" aria-label="Notificaciones" onClick={abrir}>
                <Badge badgeContent={sinLeer} color="error">
                    <NotificationsNoneIcon />
                </Badge>
            </IconButton>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { width: 360, borderRadius: 2 } }}
            >
                <Stack p={2} pb={1.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Notificaciones
                    </Typography>
                </Stack>
                <Divider />
                {mias.length === 0 ? (
                    <Stack p={3} spacing={1} alignItems="center">
                        <NotificationsNoneIcon sx={{ fontSize: 40, opacity: 0.35 }} />
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            No tienes notificaciones
                        </Typography>
                    </Stack>
                ) : (
                    // Alto acotado con scroll propio: 50 avisos no pueden empujar
                    // el popover fuera de la pantalla.
                    <Stack sx={{ maxHeight: 420, overflowY: 'auto' }} divider={<Divider />}>
                        {mias.map((notification) => {
                            const eraNueva = nuevasAlAbrir.includes(notification.id as string);
                            return (
                                <Button
                                    key={notification.id}
                                    onClick={() => alTocar(notification)}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        textTransform: 'none',
                                        color: 'text.primary',
                                        borderRadius: 0,
                                        px: 2,
                                        py: 1.5,
                                        backgroundColor: eraNueva ? 'rgba(241, 37, 37, 0.06)' : 'transparent'
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start" width="100%">
                                        <Box color="text.secondary" pt={0.25}>
                                            {ICONOS[notification.type] || ICONOS[NotificationType.System]}
                                        </Box>
                                        <Stack flex={1} minWidth={0}>
                                            <Typography variant="body2" fontWeight={eraNueva ? 700 : 500}>
                                                {notification.title}
                                            </Typography>
                                            {notification.body && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {notification.body}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {cuandoFue(notification.createdAt)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Button>
                            );
                        })}
                    </Stack>
                )}
                {/* El permiso se pide AQUÍ, con un botón y un texto que explica
                    para qué — nunca al cargar la app: un permiso negado no se
                    puede volver a pedir por código. */}
                {pushStatus !== 'granted' && (
                    <>
                        <Divider />
                        <Stack p={1.5} spacing={0.5}>
                            {pushStatus === 'unsupported' && (
                                <Typography variant="caption" color="text.secondary">
                                    Este dispositivo no puede recibir avisos en pantalla.
                                </Typography>
                            )}
                            {pushStatus === 'denied' && (
                                <Typography variant="caption" color="text.secondary">
                                    Los avisos están bloqueados. Actívalos desde el candado de la barra de direcciones.
                                </Typography>
                            )}
                            {pushStatus === 'default' && (
                                <Button
                                    size="small"
                                    startIcon={<NotificationsActiveIcon />}
                                    onClick={enablePush}
                                    disabled={pushWorking}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Avisarme en este dispositivo
                                </Button>
                            )}
                        </Stack>
                    </>
                )}
            </Popover>
        </>
    );
};

export default NotificationsBell;
